import { Badge, Button, Divider, Flex, H4, Tooltip } from '@taxbit-private/cosmic'
import { useMemo, useCallback, useState, useEffect } from 'react'
import styled from 'styled-components'

import {
  type AirflowDagContext,
  type AirflowDagNextRun,
  type AirflowDagRunsResponse,
} from 'api/types/gcDagApiTypes'
import { PendingChangesResponse } from 'api/types/pendingChangesApiTypes'
import { OrgStatus } from 'components/admin-navbar/org-select/OrgMetadataSelect'
import PermissionTooltip, { RefWrapper } from 'components/permission-tooltip/PermissionTooltip'
import Permission from 'constants/Permissions'
import {
  useTriggerGcDag,
  useFetchGcDagRuns,
  useFetchPendingChanges,
} from 'hooks/data/gc-dags/gcDagHooks'
import { useGetOrgMetadata } from 'hooks/data/orgsHook'
import useAppStore from 'store/useAppStore'

import {
  getActiveDagRun,
  getMostRecentFailedDagRun,
  getLastSuccessfulDagRun,
  checkHasPendingChanges,
  checkIsFailedState,
  computeProgressData,
  type ProgressData,
} from './dataStatusUtils'
import FailedStateStepper from './FailedStateStepper'
import InProgressStateStepper from './InProgressStateStepper'
import SuccessStateStepper from './SuccessStateStepper'
import WarningStateStepper from './WarningStateStepper'

const PopoverContainer = styled(Flex)`
  max-height: ${({ theme }) => theme.measure.xxl};
`

const ScrollableContent = styled.div`
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.m};
`

const ButtonWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
`

const FullWidthButton = styled(Button)`
  width: 100%;
`

type DataStatusPopoverContentProps = {
  dagRunsData?: AirflowDagRunsResponse
  pendingChangesData?: PendingChangesResponse
  nextRunData?: AirflowDagNextRun
}

const DataStatusPopoverContent = ({
  dagRunsData,
  pendingChangesData,
  nextRunData,
}: DataStatusPopoverContentProps) => {
  const { mutate: triggerGcDag, isLoading: isTriggering } = useTriggerGcDag()
  const addCosmicToast = useAppStore((store) => store.addCosmicToast)
  const { data: orgMetadata } = useGetOrgMetadata()
  const isOrgInactive = orgMetadata?.status === OrgStatus.Inactive

  // Keep data fresh while popover is open via polling side effects
  useFetchGcDagRuns({ refetchInterval: 60_000 })
  useFetchPendingChanges({ refetchInterval: 60_000 })

  const activeDagRun = useMemo(() => getActiveDagRun(dagRunsData?.dag_runs), [dagRunsData])

  const mostRecentFailedDagRun = useMemo(
    () => getMostRecentFailedDagRun(dagRunsData?.dag_runs),
    [dagRunsData]
  )

  const lastSuccessfulDagRun = useMemo(
    () => getLastSuccessfulDagRun(dagRunsData?.dag_runs),
    [dagRunsData]
  )

  const hasPendingChanges = useMemo(
    () => checkHasPendingChanges(pendingChangesData),
    [pendingChangesData]
  )

  const statusState = useMemo(() => {
    if (activeDagRun) {
      return 'in-progress'
    }

    if (checkIsFailedState(mostRecentFailedDagRun, lastSuccessfulDagRun)) {
      return 'failed'
    }

    if (hasPendingChanges) {
      return 'warning'
    }
    return 'success'
  }, [activeDagRun, mostRecentFailedDagRun, lastSuccessfulDagRun, hasPendingChanges])

  const [progressData, setProgressData] = useState<ProgressData | null>(() =>
    computeProgressData(statusState, pendingChangesData, activeDagRun)
  )

  useEffect(() => {
    if (statusState !== 'in-progress' || !pendingChangesData || !activeDagRun) {
      setProgressData(null)
      return
    }
    if (!activeDagRun.start_date || !pendingChangesData.average_dag_run_time_seconds) {
      setProgressData(null)
      return
    }

    const tick = () =>
      setProgressData(computeProgressData(statusState, pendingChangesData, activeDagRun))
    tick()
    const interval = setInterval(tick, 5_000)
    return () => clearInterval(interval)
  }, [statusState, pendingChangesData, activeDagRun])

  const handleUpdateNow = useCallback(() => {
    const context: AirflowDagContext = { catch_up: false }

    const onError = () => {
      addCosmicToast({
        variant: 'danger',
        message:
          'Failed to reprocess data. Try again and please contact us at help@taxbit.com if this issue persists.',
      })
    }

    triggerGcDag({ context }, { onError })
  }, [triggerGcDag, addCosmicToast])

  const lastSuccessfulUpdate = useMemo(() => {
    if (lastSuccessfulDagRun) {
      return lastSuccessfulDagRun.end_date ?? lastSuccessfulDagRun.start_date ?? null
    }
    return null
  }, [lastSuccessfulDagRun])

  const mostRecentFailedUpdate = useMemo(() => {
    if (mostRecentFailedDagRun) {
      return mostRecentFailedDagRun.end_date ?? mostRecentFailedDagRun.start_date ?? null
    }
    return null
  }, [mostRecentFailedDagRun])

  const nextUpdateScheduled = nextRunData?.next_dag_run ?? null

  return (
    <PopoverContainer direction="column">
      <Flex justifyContent="space-between" padding="m">
        <H4>Data Status</H4>
        {statusState === 'success' && <Badge label="Up to date" variant="success" />}
        {statusState === 'warning' && <Badge label="Changes detected" variant="warning" />}
        {statusState === 'in-progress' && <Badge label="Updating" variant="primary" />}
        {statusState === 'failed' && <Badge label="Update failed" variant="danger" />}
      </Flex>

      <Divider />

      <ScrollableContent>
        {statusState === 'in-progress' && (
          <InProgressStateStepper
            progressData={progressData}
            lastSuccessfulUpdate={lastSuccessfulUpdate ?? undefined}
            currentDagRunStartTime={activeDagRun?.start_date ?? undefined}
          />
        )}
        {statusState === 'failed' && (
          <FailedStateStepper
            lastSuccessfulUpdate={lastSuccessfulUpdate ?? undefined}
            mostRecentFailedUpdate={mostRecentFailedUpdate ?? undefined}
            nextUpdateScheduled={nextUpdateScheduled ?? undefined}
          />
        )}
        {statusState === 'warning' && (
          <WarningStateStepper
            lastSuccessfulUpdate={lastSuccessfulUpdate ?? undefined}
            nextUpdateScheduled={nextUpdateScheduled ?? undefined}
            pendingChangesData={pendingChangesData}
          />
        )}
        {statusState === 'success' && (
          <SuccessStateStepper
            lastSuccessfulUpdate={lastSuccessfulUpdate ?? undefined}
            nextUpdateScheduled={nextUpdateScheduled ?? undefined}
          />
        )}
      </ScrollableContent>

      <Divider />

      <ButtonWrapper>
        <PermissionTooltip permissionName={Permission.TransactionDataEdit} placement="top">
          {({ userHasPermission }) => {
            const isDisabled =
              statusState === 'in-progress' || isTriggering || !userHasPermission || isOrgInactive

            // PermissionTooltip only wraps Tooltip when the user lacks permission; show why the
            // button is disabled for permitted users (inactive org, in-progress).
            const disabledReasonTooltip =
              userHasPermission && isDisabled
                ? isOrgInactive
                  ? 'Disabled for inactive organizations'
                  : statusState === 'in-progress' || isTriggering
                    ? 'Disabled while update is in progress'
                    : undefined
                : undefined

            const button = (
              <FullWidthButton
                iconName="refresh"
                variant="button-secondary"
                trackingId="data-status-update-button"
                label="Update now"
                onClick={handleUpdateNow}
                isDisabled={isDisabled}
                isLoading={isTriggering}
              />
            )

            return disabledReasonTooltip ? (
              <Tooltip
                placement="top"
                content={disabledReasonTooltip}
                target={<RefWrapper>{button}</RefWrapper>}
              />
            ) : (
              button
            )
          }}
        </PermissionTooltip>
      </ButtonWrapper>
    </PopoverContainer>
  )
}

export default DataStatusPopoverContent
